import {
  Config,
  CRMAccountSyncCheckpoint,
  CRMAccountSyncStatus,
  TableEnum,
} from '../../CRMIndexedDBTypes';
import { IndexDbCrm, Singleton as CRMDB } from '../../CRMIndexedDB';
import { VeevaVaultAPIService } from './VeevaVaultAPIService';

type VeevaVaultAccountSyncerConfig = {
  apiService: Pick<VeevaVaultAPIService, 'getCRMAccountsPage'>;
  crmDB: Pick<
    IndexDbCrm,
    'getAccountSyncCheckpoint' | 'persistAccountSyncPage' | 'getById' | 'upsert'
  >;
  onProgress: (checkpoint: CRMAccountSyncCheckpoint) => void;
};

type VeevaVaultAccountSyncOptions = {
  resumeOnly?: boolean;
  shouldContinue?: () => boolean;
};

const ACCOUNT_SYNC_ERROR_MESSAGE = 'Account synchronization paused. Retry to continue.';

export class VeevaVaultAccountSyncer {
  private apiService: VeevaVaultAccountSyncerConfig['apiService'];
  private crmDB: VeevaVaultAccountSyncerConfig['crmDB'];
  private onProgress: VeevaVaultAccountSyncerConfig['onProgress'];

  constructor(config: Partial<VeevaVaultAccountSyncerConfig> = {}) {
    this.apiService = config.apiService || new VeevaVaultAPIService();
    this.crmDB = config.crmDB || CRMDB;
    this.onProgress = config.onProgress || (() => undefined);
  }

  private updateLastSyncTime = async (crmUserId: string): Promise<void> => {
    const config = await this.crmDB.getById<Config>(TableEnum.CONFIG_OBJECT, crmUserId);

    if (config) {
      await this.crmDB.upsert(TableEnum.CONFIG_OBJECT, {
        ...config,
        lastSyncTime: new Date().getTime(),
      });
    }
  };

  sync = async (
    crmUserId: string,
    { resumeOnly = false, shouldContinue = () => true }: VeevaVaultAccountSyncOptions = {},
  ): Promise<void> => {
    const previousCheckpoint = await this.crmDB.getAccountSyncCheckpoint(crmUserId);
    if (!shouldContinue()) {
      return;
    }

    const isResuming = previousCheckpoint?.status === CRMAccountSyncStatus.SYNCING ||
      previousCheckpoint?.status === CRMAccountSyncStatus.ERROR;
    if (resumeOnly) {
      if (!isResuming || !previousCheckpoint) {
        return;
      }

      this.onProgress(previousCheckpoint);
      if (previousCheckpoint.status === CRMAccountSyncStatus.ERROR) {
        return;
      }
    }

    let nextPage = isResuming ? previousCheckpoint?.nextPage : undefined;
    let syncedCount = isResuming ? previousCheckpoint?.syncedCount || 0 : 0;
    let total = isResuming ? previousCheckpoint?.total : undefined;
    let hasCompleted = false;

    try {
      do {
        if (!shouldContinue()) {
          return;
        }

        const page = await this.apiService.getCRMAccountsPage(nextPage);
        if (!shouldContinue()) {
          return;
        }

        const checkpoint = {
          nextPage: page.nextPage,
          status: page.nextPage ? CRMAccountSyncStatus.SYNCING : CRMAccountSyncStatus.COMPLETED,
          syncedCount: syncedCount + page.accounts.length,
          total: page.total,
        };

        await this.crmDB.persistAccountSyncPage(crmUserId, page.accounts, checkpoint);
        if (!shouldContinue()) {
          return;
        }

        nextPage = checkpoint.nextPage;
        syncedCount = checkpoint.syncedCount;
        total = checkpoint.total;
        hasCompleted = checkpoint.status === CRMAccountSyncStatus.COMPLETED;
        this.onProgress({ id: crmUserId, ...checkpoint });
      } while (nextPage);

      if (!shouldContinue()) {
        return;
      }

      await this.updateLastSyncTime(crmUserId);
    } catch (error) {
      if (!shouldContinue() || hasCompleted) {
        throw error;
      }

      const checkpoint = {
        nextPage,
        status: CRMAccountSyncStatus.ERROR,
        syncedCount,
        total,
        error: ACCOUNT_SYNC_ERROR_MESSAGE,
      };

      await this.crmDB.persistAccountSyncPage(crmUserId, [], checkpoint);
      if (!shouldContinue()) {
        return;
      }

      this.onProgress({ id: crmUserId, ...checkpoint });
      throw error;
    }
  };
}
